import { ChevronRight, Folder, FolderOpen } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import type { OverviewItem } from '../api-types';
import { ProgressBar } from './ProgressBar';

/**
 * «Unità e cartelle»: the breadcrumb of where you are, and the folders below it with their
 * progress. A folder opens by the address (`?path=`), so back and forward and a shared link work.
 */
export function FolderTree({
  items,
  path,
  linkFor,
}: {
  readonly items: readonly OverviewItem[];
  readonly path: readonly { readonly id: string | null; readonly name: string }[];
  readonly linkFor: (id: string | undefined) => string;
}) {
  const { t } = useTranslation('archive');
  const folders = items.filter((item) => item.kind === 'folder');
  return (
    <nav aria-label={t('overview.drives')} className="bg-card border-border rounded-md border p-3">
      <h3 className="text-muted-foreground px-2 pb-2 text-xs font-semibold tracking-wide uppercase">
        {t('overview.drives')}
      </h3>
      <ol
        aria-label={t('overview.breadcrumb')}
        className="flex list-none flex-wrap items-center gap-1 p-0 pb-2 text-sm"
      >
        <li>
          <Link
            className="inline-flex min-h-11 items-center px-2 underline"
            to={linkFor(undefined)}
          >
            {t('overview.root')}
          </Link>
        </li>
        {path.map((crumb, index) => (
          <li key={crumb.id ?? `hidden-${index}`} className="flex items-center">
            <ChevronRight aria-hidden="true" className="text-muted-foreground size-4" />
            {index === path.length - 1 || crumb.id === null ? (
              <span
                aria-current={index === path.length - 1 ? 'page' : undefined}
                aria-label={crumb.id === null ? t('overview.hiddenFolder') : undefined}
                className={index === path.length - 1 ? 'px-2 font-semibold' : 'px-2'}
              >
                {crumb.name}
              </span>
            ) : (
              <Link
                className="inline-flex min-h-11 items-center px-2 underline"
                to={linkFor(crumb.id)}
              >
                {crumb.name}
              </Link>
            )}
          </li>
        ))}
      </ol>
      {folders.length === 0 ? (
        <p className="text-muted-foreground px-2 text-sm">{t('overview.noFolders')}</p>
      ) : (
        <ul className="m-0 flex list-none flex-col gap-1 p-0">
          {folders.map((folder) => (
            <li key={folder.id}>
              <Link
                to={linkFor(folder.id)}
                className="hover:bg-muted flex min-h-11 flex-col gap-1.5 rounded-md px-2 py-2"
              >
                <span className="flex items-center gap-2 text-sm font-semibold">
                  {path.length === 0 ? (
                    <FolderOpen aria-hidden="true" className="size-4 shrink-0" />
                  ) : (
                    <Folder aria-hidden="true" className="size-4 shrink-0" />
                  )}
                  <span className="min-w-0 flex-1 truncate">{folder.name}</span>
                  {folder.progress ? (
                    <span className="text-muted-foreground text-xs font-normal">
                      {t('overview.inLine', folder.progress)}
                    </span>
                  ) : null}
                </span>
                {folder.progress ? <ProgressBar {...folder.progress} label={folder.name} /> : null}
              </Link>
            </li>
          ))}
        </ul>
      )}
    </nav>
  );
}
